/* Factory · Piece QR Label Sheet
 * Prints piece labels with QR codes that resolve to the piece detail page.
 * Two format modes:
 *  · A4 (default)            — 12 labels per sheet, 3×4 grid
 *  · Thermal 80mm / 58mm     — one label per receipt, page-break between pieces
 * Two source modes:
 *  · /factory/qr-labels/:grnId          — all pieces of a GRN
 *  · /factory/qr-labels?ids=id1,id2,... — arbitrary pieces (single or bulk)
 */
import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { QRCodeSVG } from "qrcode.react";

const LABELS_PER_SHEET = 12;   // 3 cols × 4 rows on A4
const THERMAL_WIDTHS = [58, 80];  // mm — common shop-floor rolls

// Build the URL a scanner should open. We use the current origin so labels
// printed on preview & on production both resolve locally to the deployed app.
function pieceUrl(pieceId) {
  const origin = window.location.origin;
  return `${origin}/factory/piece/${pieceId}`;
}

function chunk(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

export default function QRLabelSheet() {
  const { grnId } = useParams();
  const [sp, setSp] = useSearchParams();
  const idsParam = sp.get("ids");
  const format = sp.get("format") === "thermal" ? "thermal" : "a4";
  const width = Math.max(50, Math.min(120, parseInt(sp.get("width") || "80", 10) || 80));

  const [pieces, setPieces] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      let pieceIds = [];
      if (grnId) {
        const grns = (await api.get("/factory/grns")).data;
        const g = grns.find(x => x.id === grnId);
        if (g) pieceIds = g.piece_ids || [];
      } else if (idsParam) {
        pieceIds = idsParam.split(",").filter(Boolean);
      }
      const responses = await Promise.all(
        pieceIds.map(pid => api.get(`/factory/pieces/${pid}`).then(r => r.data).catch(() => null))
      );
      setPieces(responses.filter(Boolean));
      setLoading(false);
    })();
  }, [grnId, idsParam]);

  const setFormat = (nextFormat, nextWidth) => {
    const params = new URLSearchParams(sp);
    if (nextFormat === "thermal") {
      params.set("format", "thermal");
      params.set("width", String(nextWidth || width));
    } else {
      params.delete("format");
      params.delete("width");
    }
    setSp(params, { replace: true });
  };

  const sheets = format === "a4" ? chunk(pieces, LABELS_PER_SHEET) : [];

  return (
    <div data-testid="qr-label-sheet"
      data-format={format}
      data-width={width}
      style={{ background: "#F2ECE0", minHeight: "100vh" }}>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          ${format === "a4" ? `
            @page { size: A4; margin: 10mm; }
            .sheet { page-break-after: always; background: white !important; }
            .sheet:last-child { page-break-after: auto; }
          ` : `
            @page { size: ${width}mm auto; margin: 3mm; }
            .thermal-lbl { page-break-after: always; background: white !important; }
            .thermal-lbl:last-child { page-break-after: auto; }
          `}
        }
        .sheet { background: white; padding: 8mm; }
        .lbl {
          border: 1px dashed #1B3A5C55;
          padding: 6mm;
          text-align: center;
          break-inside: avoid;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: flex-start;
          gap: 2mm;
        }
        .grid12 {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          grid-auto-rows: 62mm;
          gap: 3mm;
        }
        .thermal-lbl {
          width: ${width}mm;
          margin: 4mm auto;
          padding: 3mm;
          background: white;
          text-align: center;
          border: 1px dashed #1B3A5C55;
          box-sizing: border-box;
        }
        .thermal-header {
          font-family: 'Fraunces', serif;
          font-size: ${width >= 80 ? 11 : 9}px;
          font-weight: 700;
          color: #0B2340;
          letter-spacing: 1px;
          padding-bottom: 2mm;
          border-bottom: 1px solid #1B3A5C22;
          margin-bottom: 2mm;
        }
      `}</style>

      <div className="no-print flex flex-wrap items-center gap-3 p-4 border-b bg-white">
        <button data-testid="qr-print-btn"
          onClick={() => window.print()}
          className="px-4 py-2 rounded-full text-white text-sm font-semibold"
          style={{ background: "#1B3A5C" }}>
          Print
        </button>
        <div className="flex items-center gap-1" data-testid="qr-format-toggle">
          <button onClick={() => setFormat("a4")}
            data-testid="qr-format-a4"
            className={`px-3 py-1.5 rounded-full text-xs font-semibold ${format === "a4" ? "text-white" : "text-[#1B3A5C] border border-[#1B3A5C]/20"}`}
            style={format === "a4" ? { background: "#1B3A5C" } : {}}>
            A4 sheet
          </button>
          {THERMAL_WIDTHS.map(w => (
            <button key={w} onClick={() => setFormat("thermal", w)}
              data-testid={`qr-format-thermal-${w}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold ${format === "thermal" && width === w ? "text-white" : "text-[#1B3A5C] border border-[#1B3A5C]/20"}`}
              style={format === "thermal" && width === w ? { background: "#B08B49" } : {}}>
              Thermal {w}mm
            </button>
          ))}
        </div>
        <span className="text-xs text-[#7C8E9E] ml-auto">
          {loading ? "Loading…" :
            format === "a4"
              ? `${pieces.length} piece${pieces.length === 1 ? "" : "s"} · ${sheets.length} A4 sheet${sheets.length === 1 ? "" : "s"} · scan QR opens the piece lifecycle`
              : `${pieces.length} label${pieces.length === 1 ? "" : "s"} · ${width}mm thermal roll · one label per receipt`}
        </span>
      </div>

      {loading && (
        <div className="p-6 text-sm text-[#7C8E9E] no-print">Loading pieces…</div>
      )}

      {/* A4 mode */}
      {format === "a4" && sheets.map((sheet, idx) => (
        <div key={idx} className="sheet" data-testid={`qr-sheet-${idx}`}>
          <div className="grid12">
            {sheet.map(p => (
              <div key={p.id} className="lbl" data-testid={`qr-label-${p.id}`}>
                <QRCodeSVG value={pieceUrl(p.id)} size={100} level="M" />
                <div style={{ fontFamily: "monospace", fontWeight: 700, fontSize: 12, color: "#0B2340" }}>
                  {p.code}
                </div>
                <div style={{ fontSize: 10, color: "#4A627A" }}>
                  {p.design_code} · {p.size_text}
                </div>
                <div style={{ fontSize: 9, color: "#7C8E9E" }}>
                  {p.colour} · {p.current_stage}
                </div>
                {p.tana && p.paugazi && (
                  <div style={{ fontSize: 9, color: "#7C8E9E" }}>
                    T{p.tana} × P{p.paugazi}
                  </div>
                )}
              </div>
            ))}
            {sheet.length < LABELS_PER_SHEET &&
              Array.from({ length: LABELS_PER_SHEET - sheet.length }).map((_, i) => (
                <div key={`empty-${i}`} />
              ))}
          </div>
        </div>
      ))}

      {/* Thermal mode — one label per print page */}
      {format === "thermal" && pieces.map(p => (
        <div key={p.id} className="thermal-lbl" data-testid={`qr-thermal-${p.id}`}>
          <div className="thermal-header">HAG CARPETS · MIRZAPUR</div>
          <QRCodeSVG
            value={pieceUrl(p.id)}
            size={width >= 80 ? 140 : 100}
            level="M"
            includeMargin={false}
          />
          <div style={{ fontFamily: "monospace", fontWeight: 800, fontSize: width >= 80 ? 14 : 11, color: "#0B2340", marginTop: "2mm" }}>
            {p.code}
          </div>
          <div style={{ fontSize: width >= 80 ? 10 : 8, color: "#4A627A", marginTop: "1mm" }}>
            {p.design_code} · {p.size_text}
          </div>
          <div style={{ fontSize: width >= 80 ? 9 : 8, color: "#4A627A" }}>
            {p.colour}
          </div>
          <div style={{ fontSize: width >= 80 ? 8 : 7, color: "#7C8E9E", marginTop: "1mm", textTransform: "uppercase", letterSpacing: "0.5px" }}>
            {p.current_stage}
          </div>
          {p.tana && p.paugazi && (
            <div style={{ fontSize: width >= 80 ? 8 : 7, color: "#7C8E9E" }}>
              Quality: {p.tana} × {p.paugazi}
            </div>
          )}
          <div style={{ fontSize: width >= 80 ? 7 : 6, color: "#B0B7BF", marginTop: "2mm", borderTop: "1px dotted #1B3A5C22", paddingTop: "1mm" }}>
            Scan to open piece lifecycle
          </div>
        </div>
      ))}

      {!loading && pieces.length === 0 && (
        <div className="p-6 text-sm text-[#7C8E9E]">
          No pieces to print.
          {grnId && " Check the GRN has piece records."}
          {idsParam && " Check the piece IDs in the URL."}
        </div>
      )}
    </div>
  );
}

